import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { Card } from 'react-bootstrap';
import { useChatContext } from 'providers/ChatProvider';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  faChevronDown,
  faChevronLeft,
  faCheck,
  faEnvelope // 🚀 Ícono para "No leído"
} from '@fortawesome/free-solid-svg-icons';
import { SET_CHAT_STATE } from 'reducers/ChatReducer';

// 1️⃣ Definimos las interfaces para evitar el uso de 'any'
interface Proceso {
  id: string | number;
  proc: string | number;
  nombre_rol: string;
  etiqueta: string;
}

interface ChatUser {
  id: string | number;
  name: string;
  procesos?: Proceso[]; // El signo '?' indica que puede ser opcional
}

const ChatContentHeader = () => {
  const {
    conversations, // 🚀 IMPORTANTE: Extraemos la lista completa
    chatDispatch,  // 
    currentConversation,
    setShowConversationDetails,
    setShowUserListOffcanvas
  } = useChatContext();
  const [isResolving, setIsResolving] = useState(false);
  const [isMarkingUnread, setIsMarkingUnread] = useState(false); // 🚀 Nuevo estado
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();


// Función: Resolver y Cerrar
  const handleResolve = async () => {
    if (!currentConversation) return;
    setIsResolving(true);

    try {
      await axios.put(`${API_URL}/whatsapps/conversaciones/${currentConversation.id}/estado`, {
        estado: 'Resuelta'
      });

            // 🚀 MAGIA DE REACT: Actualizamos la conversación específica en la memoria
        const conversacionesActualizadas = conversations.map(c => 
        c.id === currentConversation.id ? { ...c, estado: 'Resuelta' as const } : c
      );

      // 🚀 Guardamos el nuevo array y quitamos la selección actual
      chatDispatch({
        type: SET_CHAT_STATE,
        payload: {
          conversations: conversacionesActualizadas,
          currentConversation: null // Cerramos el chat visualmente
        }
      });
      
      // 🚀 Salimos a la bandeja principal usando React Router
      navigate('/app/apps/chat');
      
      // 💡 PRO TIP: Si notas que al usar navigate() la conversación no desaparece 
      // de la barra lateral izquierda automáticamente (porque no hubo recarga dura de página),
      // usa esta línea en lugar de navigate:
      // window.location.href = '/app/apps/chat';

    } catch (error) {
      console.error("Error al resolver la conversación", error);
    } finally {
      setIsResolving(false);
    }
  };

  // Función: Marcar como no leído (Pendiente) y salir
// Función: Marcar como no leído (Pendiente) y salir
  const handleMarkUnread = async () => {
    if (!currentConversation) return;
    setIsMarkingUnread(true);

    try {
      // 🚀 Llamamos a un NUEVO endpoint dedicado a marcar como no leído
      await axios.put(`${API_URL}/whatsapps/mark-as-unread/${currentConversation.id}`);

      // 🚀 MAGIA DE REACT: Actualizamos el estado y el último mensaje recibido
      const conversacionesActualizadas = conversations.map(c => {
        if (c.id === currentConversation.id) {
          // Clonamos los mensajes para modificarlos con seguridad
          const mensajesActualizados = [...(c.messages || [])];
          
          // Recorremos de atrás para adelante buscando el último recibido
          for (let i = mensajesActualizados.length - 1; i >= 0; i--) {
            if (mensajesActualizados[i].type === 'received') {
              mensajesActualizados[i] = {
                ...mensajesActualizados[i],
                readAt: null, // 🚀 Volvemos la fecha a nulo
                seen: false   // 🚀 Lo marcamos como no visto
              };
              break; // Solo modificamos el último y cortamos el bucle
            }
          }

          return { 
            ...c, 
            estado: 'Pendiente' as const, 
            messages: mensajesActualizados 
          };
        }
        return c;
      });
      
      chatDispatch({
        type: SET_CHAT_STATE,
        payload: {
          conversations: conversacionesActualizadas,
          currentConversation: null
        }
      });
      
      navigate('/app/apps/chat');

    } catch (error) {
      console.error("Error al marcar como pendiente y no leído", error);
    } finally {
      setIsMarkingUnread(false);
    }
  };


  // 2️⃣ Casteamos el usuario de la conversación a nuestro tipo ChatUser de forma segura
  const currentUser = currentConversation?.user as unknown as ChatUser | undefined;

  const firstName = useMemo(() => {
    return currentUser?.name.split(' ')[0] || '';
  }, [currentUser]);

  if (currentUser?.procesos) {
    console.log("Procesos del usuario:", currentUser.procesos);
  }

  return (
    <>
      {currentConversation && currentUser && (
        <>
          {/* Se añade flex-wrap y gap para que en pantallas pequeñas no se amontone */}
          <Card.Header className="p-3 p-md-4 d-flex flex-between-center flex-wrap gap-3">
            <div className="d-flex align-items-center flex-wrap gap-3 w-100">
              
              <Button
                className="ps-0 pe-2 text-body-tertiary d-sm-none"
                onClick={() => setShowUserListOffcanvas(true)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
              
              <div className="d-flex flex-column flex-md-row align-items-md-center">
                <Button
                  className="fs-7 fw-semibold text-body-emphasis d-flex align-items-center p-0 me-3 text-start"
                  onClick={() => setShowConversationDetails(true)}
                >
                  <span>{firstName}</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="ms-2 fs-10"
                  />
                </Button>

                {/* 3️⃣ Renderizado seguro y tipado de los procesos */}
                {currentUser.procesos && currentUser.procesos.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2 mt-md-0">
                    {currentUser.procesos.map((proceso) => (
                      <span 
                        key={proceso.id} 
                        className="badge bg-info text-dark shadow-sm hover-lift"
                        style={{ cursor: 'pointer', transition: 'all 0.2s', fontSize: '0.75rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/apps/project-management/proceso-details/${proceso.proc}`);
                        }} 
                        title="Clic para ir al expediente"
                      >
                        {proceso.nombre_rol} en {proceso.etiqueta}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* 🚀 Grupo de Botones: Arriba a la izquierda (justo después del nombre/etiquetas) */}
              <div className="d-flex align-items-center gap-2 ms-md-4 mt-3 mt-md-0">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={handleMarkUnread}
                  disabled={isMarkingUnread || isResolving}
                  className="rounded-pill px-3"
                >
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                  {isMarkingUnread ? 'Marcando...' : 'Marcar como no leído'}
                </Button>

                <Button 
                  variant="success" 
                  size="sm" 
                  onClick={handleResolve}
                  disabled={isResolving || isMarkingUnread}
                  className="rounded-pill px-3"
                >
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  {isResolving ? 'Resolviendo...' : 'Resolver y Cerrar'}
                </Button>
              </div>

            </div>
          </Card.Header>
        </>
      )}
    </>
  );
};

export default ChatContentHeader;